import { useQuery } from '@apollo/client/react';
import { Menu } from '@mui/material';
import React, { FC } from 'react';
import styled from 'styled-components';
import {
  FitClientBasicsFragment,
  MemberClientDocument,
} from '../../graphql/types';
import { SplitFlags } from '../../hooks/featureFlags';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import useTracking from '../../hooks/useTracking';
import { GRAY200, HOVER } from '../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { InlineShowMore } from '../reusable/ShowMore/Inline';
import Text, { TextKind } from '../reusable/Text';

export interface TemplateDefinition {
  title: string;
  message: string;
}

export enum TemplateOption {
  MEMBER_FIRST_NAME = 'MEMBER_FIRST_NAME',
  COACH_FIRST_NAME = 'COACH_FIRST_NAME',
}

type TemplateOptions = Record<TemplateOption, string | undefined | null>;

const buildGreeting = (memberName: string | undefined | null) =>
  memberName ? `Hi, ${memberName}!` : 'Hi!';

const buildSignOff = (coachName: string | undefined | null) =>
  coachName ? ` - Coach ${coachName}` : ' - Coach';

export const getChatTemplates = (
  greeting: string,
  signOff: string,
  templateOptions: TemplateOptions,
): TemplateDefinition[] => [
  {
    title: 'Notifications',
    message: `${greeting} One quick tip: Make sure your notifications are turned on.

They make it easier to stay on top of your progress without having to check the app. Plus, you'll get my chats right on your phone's home screen.

You can turn them on here: https://fit.teamexos.com/notifications

${signOff}`,
  },
  {
    title: 'Schedule First Session',
    message:
      'Looking for a personalized plan to achieve your goals? 💪 Click below to schedule a 30-minute complimentary Coaching Session 📅  https://fit.teamexos.com/schedule/services?section=coaching',
  },
  {
    title: 'Reminder Upcoming Coaching Session',
    message: `We've got a Coaching Session coming soon!  I'm excited to chat about your goals 💪 

If you have questions or need to reschedule, feel free to reach out. 
  
Looking forward to it! 😀`,
  },
  {
    title: 'Coaching Session Follow Up',
    message:
      'Hi! I wanted to follow-up with you... How are things going since our session?',
  },
  {
    title: 'No Show',
    message:
      "Hey there! I missed you today for our Coaching Session. Let's try to reschedule. Click below to book another session 📅 Hope to see you soon! 😀 https://fit.teamexos.com/schedule/services?section=coaching",
  },
  {
    title: 'QA Recording Approval',
    message: `${
      templateOptions[TemplateOption.MEMBER_FIRST_NAME] ?? 'Hello'
    }, I want to ask permission prior to our 1 on 1 coaching session if I can record our meeting as part of the Exos Quality Assurance Process. Even the coaches at Exos get coached! Part of that process entails reviewing the execution of my skills with my manager. We will delete the recording within 30 days after the review process. My manager would be the only other person seeing this. Please let me know if you are or are not ok with recording the session. We will meet for our 1 on 1 session either way and I can always find another member to record for this quality assurance process.`,
  },
];

const getPtChatTemplates = (clientId: string): TemplateDefinition[] => {
  switch (clientId) {
    case 'f485e965-d3f5-47be-8a0b-7c48a1070ac3': // J&J
      return [
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: https://buy.stripe.com/28o6st7Wyekc3wA5kn`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: https://buy.stripe.com/4gw7wxdgSgsk6IM3ce`,
        },
        {
          title: 'Personal Training - Stripe - 10 Sessions',
          message: `Use this link to purchase a package of 10 Personal Training Sessions: https://buy.stripe.com/fZeeYZ1ya5NG3wA289`,
        },
      ];
    case '0e3bbdd8-b1ee-4860-bd57-ac73f53f80ce': // AmeriHealth
      return [
        {
          title: 'Personal Training - Intro - 3 Sessions',
          message: `Use this link to purchase the Introductory Package of 3 Personal Training Sessions (for $99): https://buy.stripe.com/8wM2aedRu8CEbmM5kn`,
        },
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: https://buy.stripe.com/bIYg146p25qs0I88wy`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: https://buy.stripe.com/dR6aGKeVy0680I89AB`,
        },
        {
          title: 'Personal Training - Stripe - 10 Sessions',
          message: `Use this link to purchase a package of 10 Personal Training Sessions: https://buy.stripe.com/8wMeX0aFi1aceyYcMM`,
        },
      ];
    case 'c28d8b5b-14c8-481d-9277-c58ceae4a25e': // Pfizer
      return [
        {
          title: 'Personal Training - Intro - 3 Sessions',
          message: `Use this link to purchase the Introductory Package of 3 Personal Training Sessions (for $123): https://buy.stripe.com/8x2eVeddF0pegEl1EGg3600`,
        },
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: https://buy.stripe.com/fZufZigpR2xm9bTbfgg3603`,
        },
        {
          title: 'Personal Training - Stripe - 3 Sessions',
          message: `Use this link to purchase a package of 3 Personal Training Sessions: https://buy.stripe.com/cNiaEY7Tl7RG87Persg3602`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: https://buy.stripe.com/14A5kE6Pheg473L4QSg3601`,
        },
      ];
    case 'd3be19bb-f000-4a88-8347-ea326d088b2f': // Exos Dev Environment
      return [
        {
          title: 'Personal Training - Intro - 3 Sessions',
          message: `Use this link to purchase the Introductory Package of 3 Personal Training Sessions: sample-intro-link`,
        },
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: sample-single-link`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: sample-five-link`,
        },
        {
          title: 'Personal Training - Stripe - 10 Sessions',
          message: `Use this link to purchase a package of 10 Personal Training Sessions: sample-ten-link`,
        },
      ];
    default:
      return [];
  }
};

const getEmployerExperienceTemplates = (
  greeting: string,
  signOff: string,
): TemplateDefinition[] => [
  {
    title: 'PC/Holistic Coaching Service Upcoming Reminder',
    message: `${greeting} Looking forward to our session!

You now have the option to use it to get started with a 6-week Gameplan — a structured plan of daily activities, lessons, and habits designed around you and your goals.

You may see a few assessments on your Home screen. Those are part of this. They help track your strengths, opportunities, and progress.

Please complete them before we meet so that we can make the most of our time together. Also, make sure you're on app version 2.83 or greater.

If you’d rather use our session to talk through questions, challenges, or anything else, that's totally cool. Just let me know. (No need to do the assessments.)

Excited to connect and support you.
${signOff}`,
  },
  {
    title: 'PC Welcome Message',
    message: `${greeting} I'm excited to see you signed up for a personalized Gameplan! This experience is all about creating an efficient day-to-day wellbeing approach that works for you.

If you haven't already, please book our coaching session here: https://fit.teamexos.com/coachsessions/coaching?copyVariant=gameplan_initial_assessment

This is where we dig into your goals and lifestyle so I can best personalize your plan.

To help us get the most of our time together, please choose your focus and complete your assessments before we meet:
https://fit.teamexos.com/performancequalityassessments

And last but not least, enable notifications so you never miss a beat. You can turn them on here:
https://fit.teamexos.com/notifications

Let me know any questions you have along the way. See you soon!`,
  },
];

const getTrainingPlanTemplates = (): TemplateDefinition[] => [
  {
    title: 'Training Plans Response',
    message: `I'd love to build a personalized Training Plan for you! To make sure it fits your goals, schedule, and equipment, could you send me these four details?

Level: (Beginner or Intermediate)

Session Length: (15, 30, or 45 minutes)

Workouts per Week: (2, 3, or 4 days/week)

Equipment Access: (No Equipment, Dumbbells Only, or Full Gym)

Once I have your answers, I'll get started on your plan and get it over to you!`,
  },
];

export const getTrainingPlanTouchPointTemplates = (
  greeting: string,
): TemplateDefinition[] => [
  {
    title: 'Training Plan Auto-Assigned',
    message: `${greeting} Congrats on kicking off your Training Plan! What's your goal for the next four weeks?

Whatever you're working toward, I'm here to help. We can personalize your plan even further and make changes as you go.

Just reply here or grab a time on my calendar: https://fit.teamexos.com/schedule/services?section=coaching`,
  },
  {
    title: 'Training Plan Inactive',
    message: `${greeting} I see it's been a bit since your last logged Training Plan workout. How are things feeling? Are you bumping into scheduling hurdles, or does the routine need a quick refresh?

Message me here or pick a time on my calendar, and we'll figure things out: https://fit.teamexos.com/schedule/services?section=coaching`,
  },
  {
    title: 'Training Plan Ending Soon',
    message: `${greeting} You've got one week left in your Training Plan! As you finish strong, let's start thinking about what comes next. What goals are top of mind for you?

Reply here or book a session so we can map out your next four weeks: https://fit.teamexos.com/schedule/services?section=coaching`,
  },
  {
    title: 'Training Plan Modification Request',
    message: `${greeting} I saw your request to make changes to your Training Plan. Tell me a little more about what's going on, and I'll figure out the best way to adjust your workouts.

You can reply here or grab a time that works for you: https://fit.teamexos.com/schedule/services?section=coaching`,
  },
];

const TemplateItem = styled.li`
  border-bottom: 1px solid var(--main-border, ${GRAY200});
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  white-space: pre-line;
  &:hover {
    background-color: var(--main-hover, ${HOVER});
  }
  &:last-child {
    border-bottom: none;
  }
`;

export const TemplateMenu: FC<{
  open: boolean;
  anchorEl: HTMLElement | null;
  chatId?: string | null;
  memberId?: string | null;
  fitClient?: FitClientBasicsFragment | null;
  onTemplateClick: (template: TemplateDefinition, index: number) => void;
  onBlur: () => void;
  templateOptions: TemplateOptions;
}> = ({
  open,
  anchorEl,
  onTemplateClick,
  onBlur,
  chatId,
  memberId,
  fitClient: initialFitClient = null,
  templateOptions,
}) => {
  const { track } = useTracking();
  const { flags } = useFeatureFlags();

  // some members queries might not include the fitClient, so we need to fetch it separately if it's not included
  const { data: clientData } = useQuery(MemberClientDocument, {
    skip: !!initialFitClient || !memberId,
    variables: {
      id: memberId ?? '',
    },
  });
  const fetchedFitClient = clientData?.user?.fitClient;

  const fitClient = initialFitClient || fetchedFitClient;

  const handleBlur = (event: React.FocusEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      onBlur();
    }
  };

  const handleTemplateClick = (template: TemplateDefinition, index: number) => {
    onTemplateClick(template, index);
    track(EventNames.TemplateSelect, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Template,
      chat_id: chatId,
      event_label: template.title,
      position: index,
    });
  };

  const handleSeeMoreClick = (
    isExpanded: boolean,
    template: TemplateDefinition,
    index: number,
  ) => {
    // If it is currently expanded, it means they clicked on See More, otherwise they clicked on See Less
    const eventName = isExpanded
      ? EventNames.SeeMoreClick
      : EventNames.SeeLessClick;
    track(eventName, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Template,
      chat_id: chatId,
      event_label: template.title,
      position: index,
    });
  };

  const greeting = buildGreeting(
    templateOptions[TemplateOption.MEMBER_FIRST_NAME],
  );
  const signOff = buildSignOff(
    templateOptions[TemplateOption.COACH_FIRST_NAME],
  );

  const templatesToDisplay = getChatTemplates(
    greeting,
    signOff,
    templateOptions,
  );

  templatesToDisplay.push(...getTrainingPlanTemplates());

  const employerExperienceEnabled = flags[SplitFlags.CH_E3];
  if (employerExperienceEnabled) {
    templatesToDisplay.push(
      ...getEmployerExperienceTemplates(greeting, signOff),
    );
  }

  templatesToDisplay.push(...getTrainingPlanTouchPointTemplates(greeting));

  // const clientPtLinks = (fitClient?.id && clientLinks[fitClient.id]) || null;
  const ptChatTemplates = getPtChatTemplates(fitClient?.id ?? '');
  if (fitClient?.usesPersonalTraining && ptChatTemplates.length > 0) {
    templatesToDisplay.push(...ptChatTemplates);
  }

  return (
    <Menu
      style={{ marginTop: -16 }}
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onBlur={handleBlur}
      MenuListProps={{
        disablePadding: true, // Disable default MUI Menu padding of 8px top and bottom
      }}
      slotProps={{
        paper: {
          style: {
            maxHeight: 94 * 3.4, // This makes the menu scrollable. 94 is the height of each template item, 3.4 shows 3 items plus a little extra to see the next item
            width: 302,
          },
        },
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 8 }}
    >
      {templatesToDisplay.map((template, index) => (
        <TemplateItem
          data-testid={`template-${index}`}
          key={template.title}
          onClick={() => handleTemplateClick(template, index)}
        >
          <Text kind={TextKind.BodySM}>{template.title}</Text>
          <InlineShowMore.BodyS
            truncationWidth={250}
            lines={2}
            onToggleClick={(isExpanded) =>
              handleSeeMoreClick(isExpanded, template, index)
            }
          >
            {template.message}
          </InlineShowMore.BodyS>
        </TemplateItem>
      ))}
    </Menu>
  );
};
